import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Empty, Pagination, Spin, Typography } from "antd";

import { SceneItems } from "../../../../components/Admin/VirtualScenes/SceneItems";
import { VirtualScenes as VirtualScenesApi } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useTranslation } from "react-i18next";

import styles from "./SceneList.module.scss";

const virtualSceneController = new VirtualScenesApi();
const { Title } = Typography;

// Tamano de pagina en cliente. El endpoint mini-creator devuelve escenas de
// varios creadores y se filtran por userId aqui; por eso paginamos en cliente
// sobre el set ya filtrado (la paginacion del servidor no aplica al subconjunto
// del docente). Solo se muestra paginacion si hay mas de PAGE_SIZE escenas
// (aprox. 5 filas), evitando paginas fantasma.
const PAGE_SIZE = 20;

export function SceneList({ reload, onReload }) {
  const { accessToken, user } = useAuth();
  const { t } = useTranslation("scenes");
  const userId = user?.user?._id;

  const [scenes, setScenes] = useState(null);
  const [page, setPage] = useState(1);
  const [notFound, setNotFound] = useState(false);

  const fetchScenes = useCallback(async () => {
    if (!accessToken || !userId) return;

    try {
      setNotFound(false);

      // Recolecta TODAS las paginas del servidor (acotado) y filtra por usuario,
      // para tener el conjunto real de escenas del docente y paginar en cliente.
      const collected = [];
      let current = 1;
      let totalPages = 1;
      const SAFETY_CAP = 25;

      do {
        const response =
          await virtualSceneController.getVirtualScenesMiniCreator(
            { page: current, limit: 100 },
            accessToken,
          );
        collected.push(...(response?.miniatureData || []));
        totalPages = response?.totalPages || 1;
        current += 1;
      } while (current <= totalPages && current <= SAFETY_CAP);

      const filtered = collected.filter((scene) => scene.userId === userId);

      if (filtered.length === 0) {
        setScenes([]);
        setNotFound(true);
        return;
      }

      setScenes(filtered);
      setPage(1);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error al cargar las escenas:", error);
      setScenes([]);
      setNotFound(true);
    }
  }, [accessToken, userId]);

  useEffect(() => {
    fetchScenes();
  }, [fetchScenes, reload]);

  const pagedScenes = useMemo(() => {
    if (!scenes) return [];
    const start = (page - 1) * PAGE_SIZE;
    return scenes.slice(start, start + PAGE_SIZE);
  }, [scenes, page]);

  const showPagination = (scenes?.length || 0) > PAGE_SIZE;

  if (scenes === null && !notFound) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    );
  }

  if (notFound || !scenes || scenes.length === 0) {
    return (
      <div className={styles.empty}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("list.noScenesCreated")}
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Title level={5} className={styles.title}>
        {t("list.scenesCreatedTitle")}
      </Title>

      <div className={styles.grid}>
        {pagedScenes.map((scene) => (
          <SceneItems key={scene.refId} scene={scene} onReload={onReload} />
        ))}
      </div>

      {showPagination ? (
        <div className={styles.pagination}>
          <Pagination
            current={page}
            pageSize={PAGE_SIZE}
            total={scenes.length}
            onChange={(nextPage) => setPage(nextPage)}
            showSizeChanger={false}
          />
        </div>
      ) : null}
    </div>
  );
}
